import type { CompiledSlotDTO, CreativeDTO, PlaylistItemDTO } from '@/types/player.types'

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function validateCreative(creative: CreativeDTO, slotId: string): void {
  if (!isNonEmptyString(creative.creativeId)) {
    throw new Error(`[schedule] campaign slot ${slotId} has creative without creativeId`)
  }
  if (!isNonEmptyString(creative.assetId)) {
    throw new Error(`[schedule] campaign slot ${slotId} has creative without assetId`)
  }
  if (!isNonEmptyString(creative.assetUrl) && !isNonEmptyString(creative.hlsUrl)) {
    throw new Error(`[schedule] campaign slot ${slotId} has creative without playable URL`)
  }
  if (!isFiniteNumber(creative.weight) || creative.weight <= 0) {
    throw new Error(`[schedule] campaign slot ${slotId} has creative with invalid weight`)
  }
  if (creative.durationSeconds !== undefined && creative.durationSeconds !== null) {
    if (!isFiniteNumber(creative.durationSeconds) || creative.durationSeconds <= 0) {
      throw new Error(`[schedule] campaign slot ${slotId} has creative with invalid durationSeconds`)
    }
  }
}

const QR_POSITIONS = new Set(['top_left', 'top_right', 'bottom_left', 'bottom_right'])

/**
 * O QR é acessório: um `qrCode` malformado sai do slot (a campanha roda sem QR) em vez de recusar a programação
 * inteira, que é o que acontece com os campos obrigatórios.
 */
function sanitizeQrCode(slot: CompiledSlotDTO): void {
  const qr = slot.qrCode
  if (qr === undefined) return
  const valid =
    typeof qr === 'object' &&
    qr !== null &&
    isNonEmptyString(qr.url) &&
    qr.url.length <= 2048 &&
    /^https?:\/\//.test(qr.url) &&
    QR_POSITIONS.has(qr.position) &&
    isFiniteNumber(qr.sizePct) &&
    qr.sizePct > 0 &&
    qr.sizePct <= 50 &&
    (qr.callToAction === undefined || typeof qr.callToAction === 'string')
  if (!valid) delete slot.qrCode
}

function validatePlaylistItem(item: PlaylistItemDTO, slotId: string): void {
  if (!isNonEmptyString(item.itemId)) {
    throw new Error(`[schedule] playlist slot ${slotId} has item without itemId`)
  }
  if (!isFiniteNumber(item.sortOrder)) {
    throw new Error(`[schedule] playlist slot ${slotId} has item with invalid sortOrder`)
  }
  if (!isFiniteNumber(item.durationSeconds) || item.durationSeconds <= 0) {
    throw new Error(`[schedule] playlist slot ${slotId} has item with invalid durationSeconds`)
  }
  if (!isNonEmptyString(item.assetUrl) && !isNonEmptyString(item.hlsUrl) && !isNonEmptyString(item.externalUrl)) {
    throw new Error(`[schedule] playlist slot ${slotId} has item without playable source`)
  }
}

function validateCampaignSlot(slot: CompiledSlotDTO): void {
  const hasOwnCampaign = isNonEmptyString(slot.campaignId)
  const hasGuestCampaign = isNonEmptyString(slot.guestCampaignId)

  if (hasOwnCampaign === hasGuestCampaign) {
    throw new Error(`[schedule] campaign slot ${slot.slotId} must have exactly one of campaignId or guestCampaignId`)
  }
  if (!isNonEmptyString(slot.campaignName)) {
    throw new Error(`[schedule] campaign slot ${slot.slotId} is missing campaignName`)
  }
  if (!Array.isArray(slot.creatives) || slot.creatives.length === 0) {
    throw new Error(`[schedule] campaign slot ${slot.slotId} is missing creatives`)
  }

  for (const creative of slot.creatives) {
    validateCreative(creative, slot.slotId)
  }
  sanitizeQrCode(slot)
}

function validatePlaylistSlot(slot: CompiledSlotDTO): void {
  if (!isNonEmptyString(slot.playlistId)) {
    throw new Error(`[schedule] playlist slot ${slot.slotId} is missing playlistId`)
  }
  if (!Array.isArray(slot.playlistItems) || slot.playlistItems.length === 0) {
    throw new Error(`[schedule] playlist slot ${slot.slotId} is missing playlistItems`)
  }

  for (const item of slot.playlistItems) {
    validatePlaylistItem(item, slot.slotId)
  }
}

export function validateSchedulePayload(payload: unknown): CompiledSlotDTO[] {
  if (!Array.isArray(payload)) {
    throw new Error('[schedule] payload must be an array')
  }

  const slots = payload as CompiledSlotDTO[]
  const seenSlotIds = new Set<string>()

  for (const slot of slots) {
    if (!isNonEmptyString(slot.slotId)) {
      throw new Error('[schedule] slot without slotId received')
    }
    if (seenSlotIds.has(slot.slotId)) {
      throw new Error(`[schedule] duplicate slotId received: ${slot.slotId}`)
    }
    seenSlotIds.add(slot.slotId)

    if (!isNonEmptyString(slot.slotName)) {
      throw new Error(`[schedule] slot ${slot.slotId} is missing slotName`)
    }
    if (!['fixed', 'campaign', 'event'].includes(slot.slotType)) {
      throw new Error(`[schedule] slot ${slot.slotId} has invalid slotType`)
    }
    if (!isFiniteNumber(slot.priority)) {
      throw new Error(`[schedule] slot ${slot.slotId} has invalid priority`)
    }

    if (slot.slotType === 'campaign') {
      validateCampaignSlot(slot)
    } else if (slot.slotType === 'event' && slot.eventAction === 'screen_off') {
      // Desligar a tela não tem conteúdo: a API manda o slot sem playlistId. Antes caía na validação de playlist e
      // a programação inteira era recusada.
      if (slot.maxDurationSeconds !== undefined && !isFiniteNumber(slot.maxDurationSeconds)) {
        throw new Error(`[schedule] screen_off slot ${slot.slotId} has invalid maxDurationSeconds`)
      }
    } else {
      validatePlaylistSlot(slot)
    }
  }

  return slots
}
